import { useQuery } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { request, requestBlob } from '@/lib/api'

/**
 * Номенклатура целиком (V60): карточка, характеристики, фото, дерево категорий.
 *
 * Поля ответа сравниваются нестрого (`== null`): сервер не отдаёт пустых полей вовсе
 * (`non_null`), и `undefined` здесь — штатное «не задано», а не ошибка.
 */

export interface ProductAttribute {
  id: string
  name: string
  value: string
  unit?: string | null
  sortOrder: number
}

export interface ProductPhoto {
  id: string
  attachmentId: string
  fileName: string
  contentType?: string | null
  sizeBytes: number
  /** Вердикт антивируса: до `clean` фото не отдаётся, и экран рисует заглушку. */
  scanStatus: 'pending' | 'clean' | 'infected' | 'failed'
  downloadable: boolean
  main: boolean
  sortOrder: number
  uploadedAt: string
}

export interface ProductCard {
  id: string
  version: number
  sku: string
  name: string
  description?: string | null
  productType: string
  categoryId?: string | null
  categoryName?: string | null
  categoryTrail?: string[]
  uomId: string
  uomCode?: string | null
  uomName?: string | null
  taxRateCode: string
  barcode?: string | null
  weightKg?: number | null
  volumeM3?: number | null
  lengthMm?: number | null
  widthMm?: number | null
  heightMm?: number | null
  batchTracked: boolean
  serialTracked: boolean
  marked: boolean
  shelfLifeDays?: number | null
  storageCondition: string
  minStock?: number | null
  reorderPoint?: number | null
  active: boolean
  /** Единица, тип и признаки учёта заморожены движениями — форма показывает их без правки. */
  accountingLocked: boolean
  attributes?: ProductAttribute[]
  photos?: ProductPhoto[]
  createdAt?: string
}

export interface ProductOptions {
  units: { id: string; code: string; name: string; kind: string }[]
  productTypes: string[]
  storageConditions: string[]
  maxPhotoBytes: number
  maxPhotos: number
}

export interface ProductCategory {
  id: string
  parentId?: string | null
  code: string
  name: string
  version: number
  trail: string[]
  products: number
  children: number
}

/**
 * Тело заведения и правки. Необязательные поля — строки: `''` очищает поле, отсутствие
 * ключа — «не менялось». Числа строкой по той же причине: пустой вес иначе уехал бы
 * `null`-ом, и сервер счёл бы его «не менявшимся».
 */
export interface ProductRequest {
  version?: number | null
  sku?: string
  name?: string
  description?: string
  productType?: string
  categoryId?: string
  uomId?: string
  taxRateCode?: string
  barcode?: string
  weightKg?: string
  volumeM3?: string
  lengthMm?: string
  widthMm?: string
  heightMm?: string
  batchTracked?: boolean
  serialTracked?: boolean
  marked?: boolean
  shelfLifeDays?: string
  storageCondition?: string
  minStock?: string
  reorderPoint?: string
  active?: boolean
}

export const fetchProduct = (id: string, signal?: AbortSignal) =>
  request<ProductCard>(`/sales/products/${id}`, { signal })

const fetchProductOptions = (signal?: AbortSignal) =>
  request<ProductOptions>('/sales/products/options', { signal })

export const createProduct = (body: ProductRequest) =>
  request<ProductCard>('/sales/products', { method: 'POST', body })

export const updateProduct = (id: string, body: ProductRequest) =>
  request<ProductCard>(`/sales/products/${id}`, { method: 'PUT', body })

export const saveAttributes = (
  id: string,
  attributes: { id?: string | null; name: string; value: string; unit?: string | null }[],
) =>
  request<{ attributes: ProductAttribute[] }>(`/sales/products/${id}/attributes`, {
    method: 'PUT',
    body: { attributes },
  }).then((response) => response.attributes)

export function uploadPhoto(id: string, file: File): Promise<ProductPhoto> {
  const body = new FormData()
  body.set('file', file)
  return request<ProductPhoto>(`/sales/products/${id}/photos`, { method: 'POST', rawBody: body })
}

export const makeMainPhoto = (id: string, photoId: string) =>
  request<{ photos: ProductPhoto[] }>(`/sales/products/${id}/photos/${photoId}/main`, { method: 'POST' })
    .then((response) => response.photos)

export const reorderPhotos = (id: string, photoIds: string[]) =>
  request<{ photos: ProductPhoto[] }>(`/sales/products/${id}/photos/order`, { method: 'PUT', body: { photoIds } })
    .then((response) => response.photos)

export const deletePhoto = (id: string, photoId: string) =>
  request<{ photos: ProductPhoto[] }>(`/sales/products/${id}/photos/${photoId}`, { method: 'DELETE' })
    .then((response) => response.photos)

const fetchCategories = (signal?: AbortSignal) =>
  request<{ categories: ProductCategory[] }>('/sales/product-categories', { signal }).then((response) => response.categories)

export const createCategory = (body: { name: string; parentId?: string | null }) =>
  request<ProductCategory>('/sales/product-categories', { method: 'POST', body })

export const updateCategory = (
  id: string,
  body: { name?: string; parentId?: string | null; toRoot?: boolean; version?: number },
) => request<ProductCategory>(`/sales/product-categories/${id}`, { method: 'PUT', body })

export const deleteCategory = (id: string) =>
  request<void>(`/sales/product-categories/${id}`, { method: 'DELETE' })

export function useProductOptions() {
  return useQuery({ queryKey: ['product-options'], queryFn: ({ signal }) => fetchProductOptions(signal), staleTime: 5 * 60_000 })
}

export function useCategories(enabled = true) {
  return useQuery({ queryKey: ['product-categories'], queryFn: ({ signal }) => fetchCategories(signal), enabled })
}

/** Категория и её глубина в дереве — для выбора отступом в `<select>` и для экрана дерева. */
export interface CategoryNode {
  category: ProductCategory
  depth: number
}

/**
 * Дерево в порядке обхода: корни по названию, под каждым — его ветка.
 *
 * Строится по `parentId`, а не по `trail`: два соседа с одинаковым путём названий быть не
 * могут, но родитель — единственное, что сервер гарантирует. Узел, чей родитель не пришёл
 * (не должно случаться — удалить категорию с потомками нельзя), показывается корнем, а не
 * пропадает.
 */
export function flattenTree(categories: ProductCategory[]): CategoryNode[] {
  const known = new Set(categories.map((category) => category.id))
  const byParent = new Map<string, ProductCategory[]>()
  for (const category of categories) {
    const parent = category.parentId != null && known.has(category.parentId) ? category.parentId : ''
    const siblings = byParent.get(parent) ?? []
    siblings.push(category)
    byParent.set(parent, siblings)
  }
  const result: CategoryNode[] = []
  const walk = (parent: string, depth: number) => {
    const siblings = [...(byParent.get(parent) ?? [])].sort((a, b) => a.name.localeCompare(b.name))
    for (const category of siblings) {
      result.push({ category, depth })
      if (depth < 32) walk(category.id, depth + 1)
    }
  }
  walk('', 0)
  return result
}

/** Идентификаторы ветки: сама категория и все её потомки — куда её переносить нельзя. */
export function subtreeOf(categories: ProductCategory[], id: string): Set<string> {
  const result = new Set<string>([id])
  let grew = true
  while (grew) {
    grew = false
    for (const category of categories) {
      if (category.parentId != null && result.has(category.parentId) && !result.has(category.id)) {
        result.add(category.id)
        grew = true
      }
    }
  }
  return result
}

/**
 * Байты фото — через приложение, с токеном (`requestBlob`): `<img src>` заголовка
 * `Authorization` не шлёт, а presigned-ссылок нет намеренно (права остаются на сервере).
 *
 * Запрашивается только `downloadable` фото: непроверенное сервер не отдаёт (404), и экран
 * рисует вместо него заглушку «проверяется», а не битую картинку. Объектный URL
 * освобождается при смене фото и при размонтировании — иначе галерея копит память.
 */
export function usePhotoUrl(photo: Pick<ProductPhoto, 'attachmentId' | 'downloadable'> | null | undefined) {
  const attachmentId = photo?.downloadable ? photo.attachmentId : null
  const blob = useQuery({
    queryKey: ['attachment-blob', attachmentId],
    enabled: attachmentId != null,
    queryFn: ({ signal }) => requestBlob(`/attachments/${attachmentId}/content`, signal),
    staleTime: Infinity,
    retry: false,
  })
  const [url, setUrl] = useState<string | null>(null)
  useEffect(() => {
    if (!blob.data) {
      setUrl(null)
      return
    }
    const created = URL.createObjectURL(blob.data)
    setUrl(created)
    return () => URL.revokeObjectURL(created)
  }, [blob.data])
  return { url, failed: blob.isError }
}
